/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { z } from 'zod'

export const ACCOUNT_SHORTCUT_SECTIONS = [
  'blocked',
  'email',
  'feed-hidden',
  'general',
  'notifications',
  'points',
] as const

export type AccountShortcutSection = (typeof ACCOUNT_SHORTCUT_SECTIONS)[number]

const profileIdSchema = z.string().uuid()
const shortcutSectionSet = new Set<string>(ACCOUNT_SHORTCUT_SECTIONS)

export function isAccountShortcutSection(section: string): section is AccountShortcutSection {
  return shortcutSectionSet.has(section)
}

export function isAccountProfileId(value: string): boolean {
  return profileIdSchema.safeParse(value).success
}

export type AccountShortcutResolution =
  | { kind: 'home' }
  | { kind: 'section'; section: AccountShortcutSection }
  | { kind: 'profile'; profileId: string }
  | { kind: 'not-found' }

export function resolveAccountShortcut(section?: string): AccountShortcutResolution {
  if (!section) {
    return { kind: 'home' }
  }

  if (isAccountShortcutSection(section)) {
    return { kind: 'section', section }
  }

  if (isAccountProfileId(section)) {
    return { kind: 'profile', profileId: section }
  }

  return { kind: 'not-found' }
}

export function accountLoginRedirectHref(redirectTo: string): string {
  return `/auth/login?redirectTo=${encodeURIComponent(redirectTo)}`
}

export function accountShortcutDestinationHref(args: {
  resolution: AccountShortcutResolution
  currentProfileId?: string | null
}): string | null {
  const { resolution, currentProfileId } = args

  if (resolution.kind === 'home') {
    if (!currentProfileId) {
      return null
    }
    return `/account/${currentProfileId}/general`
  }

  if (resolution.kind === 'section') {
    if (!currentProfileId) {
      return null
    }
    return `/account/${currentProfileId}/${resolution.section}`
  }

  if (resolution.kind === 'profile') {
    return `/account/${resolution.profileId}/general`
  }

  return null
}

export function accountShortcutUnauthenticatedRedirectTo(resolution: AccountShortcutResolution): string {
  if (resolution.kind === 'section') {
    return `/account/${resolution.section}`
  }

  if (resolution.kind === 'profile') {
    return `/account/${resolution.profileId}/general`
  }

  return '/account'
}
